import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099KInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-K instructions for Tax Year 2025"
          description="Get step-by-step instructions to complete Form 1099-K for Tax Year 2025. TaxZerone helps you report payment card and third-party network transactions correctly."
          keywords=""
          canonical="resources/information-returns/form-1099-k-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-K</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-k-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-K at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-K,{' '}
                        </strong>{' '}
                        is used to report payments received through payment card
                        transactions and third-party network transactions,
                        including credit and debit cards, stored value cards,
                        payment apps, and online marketplaces.
                      </span>
                    </li>

                    <li>
                      <span>
                        Recipient copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}> January 31</strong>
                        , and the form must be filed with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          February 28
                        </strong>{' '}
                        if filing on paper or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          March 31{' '}
                        </strong>{' '}
                        if filing electronically.{' '}
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          This article covers
                        </strong>{' '}
                        line-by-line instructions, including creditor and debtor
                        details, the identifiable event date in{' '}
                        <strong style={{ fontWeight: 600 }}> Box 1</strong>,
                        amount of debt discharged in{' '}
                        <strong style={{ fontWeight: 600 }}> Box 2</strong>,
                        interest included in the canceled debt in{' '}
                        <strong style={{ fontWeight: 600 }}>Box 3</strong>, debt
                        description in{' '}
                        <strong style={{ fontWeight: 600 }}> Box 4</strong>,
                        debtor's personal liability in{' '}
                        <strong style={{ fontWeight: 600 }}> Box 5</strong>,
                        identifiable event code in{' '}
                        <strong style={{ fontWeight: 600 }}> Box 6</strong>, and
                        the fair market value (FMV) of property in{' '}
                        <strong style={{ fontWeight: 600 }}> Box 7</strong> when{' '}
                        <strong style={{ fontWeight: 600 }}>Form 1099-C</strong>{' '}
                        is filed in combination with
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-A
                        </strong>
                        .
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}> Penalties:</strong>{' '}
                        Late or incorrect filing penalties range from
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per form
                        </strong>
                        , depending on how late the form is filed or whether
                        there is intentional disregard.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-K?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  IRS Form 1099-K reports payments received through reportable
                  payment card transactions and/or settlement of third-party
                  network transactions.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  These payments can come from various sources, including:
                </Typography>

                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Credit, debit, or stored value cards, such as gift cards
                      (payment cards)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Payment apps or online marketplaces, which are also known
                      as third-party settlement organizations.
                    </ListItemText>
                  </ListItem>
                </List>

                {/* <Alert
                  severity="info"
                  sx={{ mb: 4 }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Tip:</b> Make sure you collect W-9 forms from any
                  contractors you pay so you have the necessary information to
                  complete the 1099-NEC accurately.
                </Alert> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file Form 1099-K?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 1099-K must be completed and submitted annually to the
                  IRS by payment card companies, payment apps, and online
                  marketplaces. If your business meets the following criteria
                  within a calendar year, you must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-k/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-K
                  </Link>
                  :
                </Typography>

                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Receives payments exceeding $20,000.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Has more than 200 transactions through credit cards or
                      third-party payment networks.
                    </ListItemText>
                  </ListItem>
                </List>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-K due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must be aware of two important deadlines related to
                  Form 1099-K:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filing deadline with the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline for payers to submit Form 1099-K to the IRS
                  depends on the filing method:
                </Typography>

                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Electronic filing deadline: March 31, 2026</strong>
                    <br />
                    If you are e-filing Form 1099-K with the IRS, the deadline
                    is March 31st of the year following the tax year for which
                    the form is being issued.
                  </li>

                  <Box
                    component="div"
                    sx={{ mb: 2, p: 2, borderRadius: 2 }}
                    className="gradient-bg"
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Avoid the stress of last-minute 1099-K deadlines.
                        </Typography>
                        <Typography variant="body2">
                          E-file with TaxZerone now and enjoy hassle-free IRS
                          compliance and peace of mind.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 150,
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover, &:focus, &:active': { color: '#fff' },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099kinstructions`}
                        >
                          E-File Now
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Paper filing deadline: February 28, 2026*</strong>
                    <br /> If you are filing Form 1099-K with the IRS using
                    traditional paper methods, the deadline is February 28th of
                    the year following the tax year for which the form is
                    issued.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Payee copies deadline: January 31, 2026*
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Filers must deliver copies of Form 1099-K to payees by January
                  31st of the year following the relevant tax year. This allows
                  payees to accurately file their tax returns on time.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> * if the deadline falls on the weekend or
                  federal holiday, it is extended to next business days.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Missing these deadlines or providing incorrect information on
                  Form 1099-K can lead to IRS penalties. To avoid potential
                  penalties, filers should take steps to ensure that Form 1099-K
                  is filed and distributed both accurately and on time.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-K penalty
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-k/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 1099-K
                  </Link>{' '}
                  within the deadline, the IRS will impose a penalty. Here are
                  the Form 1099-K penalty rates:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Stack
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Why risk penalties?
                    </Typography>
                    <Typography variant="body2">
                      E-file your 1099-K forms ahead of the deadline with
                      TaxZerone.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 198,
                      textAlign: 'left',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099kinstructions`}
                    >
                      E-file Form 1099-K
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-K Instructions - How to fill out?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-K.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filer details
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-1099-k-filer-details-img.webp" />
                  <source srcSet="/assets/images/form-1099-k-filer-details-img.png" />
                  <img
                    src="/assets/images/form-1099-k-filer-details-img.png"
                    loading="lazy"
                    alt="Form 1099-k-filer details"
                    width={591}
                    height={248}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter your name or business name, and complete address.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Filer’s TIN
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-k-filer-tin-img.webp" />
                  <source srcSet="/assets/images/form-1099-k-filer-tin-img.png" />
                  <img
                    src="/assets/images/form-1099-k-filer-tin-img.png"
                    loading="lazy"
                    alt="Form 1099-k-filer tin"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
                  Enter the filer’s TIN (EIN) of the entity with the filing
                  requirement.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Payee’s TIN
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-k-payee-tin-img.webp" />
                  <source srcSet="/assets/images/form-1099-k-payee-tin-img.png" />
                  <img
                    src="/assets/images/form-1099-k-payee-tin-img.png"
                    loading="lazy"
                    alt="Form 1099-k-payee's tin"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the payee’s TIN (EIN / SSN / ATIN / ITIN).
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  FILER Checkboxes
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-k-filer-checkboxes-img.webp" />
                  <source srcSet="/assets/images/form-1099-k-filer-checkboxes-img.png" />
                  <img
                    src="/assets/images/form-1099-k-filer-checkboxes-img.png"
                    loading="lazy"
                    alt="Form 1099-k-filer checkboxes"
                    width={297}
                    height={103}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check the
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    first box if the entity with the filing requirement (payer)
                    is a PSE.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    second (bottom) box if the entity with the filing
                    requirement (payer) is an EPF or other third party.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Transactions Reported Checkboxes
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-k-transactions-reported-img.webp" />
                  <source srcSet="/assets/images/form-1099-k-transactions-reported-img.png" />
                  <img
                    src="/assets/images/form-1099-k-transactions-reported-img.png"
                    loading="lazy"
                    alt="Form 1099-k-transactions reported checkboxes"
                    width={297}
                    height={103}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check the
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    first box if you are reporting payment card transactions.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    second box if you are reporting third-party network
                    transactions.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Payee details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-k-payee-details-img.webp" />
                  <source srcSet="/assets/images/form-1099-k-payee-details-img.png" />
                  <img
                    src="/assets/images/form-1099-k-payee-details-img.png"
                    loading="lazy"
                    alt="Form 1099-k-payee details"
                    width={591}
                    height={435}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the recipient’s name and complete address.
                </Typography>

                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>PSE’S name and telephone number:</b> If the entity with
                    the filing requirement (filer) is an EPF or other third
                    party, enter the PSE&apos;s name and telephone number. The
                    telephone number must allow a payee to reach a person
                    knowledgeable about the payments reported on the form.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Account number:</b> You must provide the account number
                    if you have multiple accounts for the same recipient and
                    filing more than one Form 1099-K for that recipient.
                  </li>

                  <li style={{ marginBottom: 12 }}>
                    <b>2nd TIN not:</b> If the IRS has notified you two times
                    within the last 3 years that the payee provided an incorrect
                    TIN, you can enter an &quot;X &quot; in this box. By doing
                    so, the IRS will stop sending you further notices about this
                    account&apos;s TIN.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 3,
                    fontWeight: 600,
                  }}
                >
                  Box 1a: Gross Payment Card/Third Party Network Transactions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-k-box1a-gross-payment-img.webp" />
                  <source srcSet="/assets/images/form-1099-k-box1a-gross-payment-img.png" />
                  <img
                    src="/assets/images/form-1099-k-box1a-gross-payment-img.png"
                    loading="lazy"
                    alt="Form 1099-k-box-1a-gross payment card/third party network transactions"
                    width={245}
                    height={89}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total dollar amount of all payment card and
                  third-party network transactions for the calendar year before
                  any deductions or adjustments. This is the sum of the full
                  charge amounts for each transaction involving each payee,
                  determined on the transaction dates, without subtracting any
                  credits, fees, refunds, discounts, shipping charges, or other
                  amounts.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 1b: Card Not Present Transactions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-k-box1b-card-img.webp" />
                  <source srcSet="/assets/images/form-1099-k-box1b-card-img.png" />
                  <img
                    src="/assets/images/form-1099-k-box1b-card-img.png"
                    loading="lazy"
                    alt="Form 1099-k-box-1b-card not present transactions"
                    width={245}
                    height={74}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total dollar amount of all payment card and
                  third-party network transactions for the calendar year where
                  the physical card was not present. This includes online
                  orders, phone orders, and catalog orders. Report the full
                  charge amounts before any deductions or adjustments like
                  credits, fees, refunds, discounts, or shipping charges. Use
                  the transaction dates to determine the amounts.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 2: Merchant Category Code
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-k-box2-merchant-category-img.webp" />
                  <source srcSet="/assets/images/form-1099-k-box2-merchant-category-img.png" />
                  <img
                    src="/assets/images/form-1099-k-box2-merchant-category-img.png"
                    loading="lazy"
                    alt="Form 1099-k-box-2-merchant category code"
                    width={245}
                    height={74}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the 4-digit Merchant Category Code (MCC) that classifies
                  the type of business the payee operates for the payment card
                  transactions you are reporting on Form 1099-K. If you use a
                  classification system other than MCCs, select the MCC that
                  best describes the payee&apos;s primary business activity.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 3: Number of Payment Transactions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-k-box3-number-payment-img.webp" />
                  <source srcSet="/assets/images/form-1099-k-box3-number-payment-img.png" />
                  <img
                    src="/assets/images/form-1099-k-box3-number-payment-img.png"
                    loading="lazy"
                    alt="Form 1099-k-box-3-number of payment transactions"
                    width={245}
                    height={74}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the number of payment transactions processed through the
                  payment card/third-party payer network.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 4: Federal income tax withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-k-box4-federal-tax-img.webp" />
                  <source srcSet="/assets/images/form-1099-k-box4-federal-tax-img.png" />
                  <img
                    src="/assets/images/form-1099-k-box4-federal-tax-img.png"
                    loading="lazy"
                    alt="Form 1099-k-box-4-federal income tax withheld"
                    width={247}
                    height={74}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report any federal income tax withheld from payments reported.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Withholding is required if recipients have not
                  provided their TIN.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 5a–5l
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-k-boxes5a-5i-img.webp" />
                  <source srcSet="/assets/images/form-1099-k-boxes5a-5i-img.png" />
                  <img
                    src="/assets/images/form-1099-k-boxes5a-5i-img.png"
                    loading="lazy"
                    alt="Form 1099-k-box-5a-5l"
                    width={487}
                    height={368}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the gross amount of the total reportable payment
                  transactions for each month of the calendar year.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6–8: State information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-k-box6-8-state-img.webp" />
                  <source srcSet="/assets/images/form-1099-k-box6-8-state-img.png" />
                  <img
                    src="/assets/images/form-1099-k-box6-8-state-img.png"
                    loading="lazy"
                    alt="Form 1099-k-box-6-8-state information"
                    width={679}
                    height={90}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Box 6:</b> Enter the 2-letter state abbreviation for the
                  state
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Box 7:</b> Enter the state identification number
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b> Box 8:</b> Report any state income tax withheld from the
                  payment
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to File Form 1099-K
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can file Form 1099-K, either electronically or by mail.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic Filing (E-filing)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  E-filing is the IRS-recommended method due to its efficiency,
                  accuracy, and quick processing notifications.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  With TaxZerone , you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-k/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-file Form 1099-K
                  </Link>{' '}
                  which you can complete within minutes. Here’s how it works:
                </Typography>

                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter the required information, including:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Payer&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Recipient&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>Transaction details</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Review all entered information for accuracy.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Transmit the form securely to the IRS.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Provide a copy to the recipient.
                  </li>
                </ol>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  While less efficient, paper filing remains an option for those
                  who prefer it. Here are the steps for paper filing:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Steps for paper filing:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download Form 1099-K from the official{' '}
                    <Link
                      href="https://www.irs.gov"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      IRS website
                    </Link>
                    .
                  </li>
                  <li style={{ marginBottom: 16 }}>Print the form.</li>
                  <Alert
                    severity="warning"
                    icon={false}
                    sx={{
                      mb: 3,
                      mt: 0,
                      borderLeft: '3px solid rgb(102, 60, 0)',
                    }}
                  >
                    <b> Note:</b> The IRS requires special red ink forms for
                    submission. You can order these from the IRS or approved
                    vendors.
                  </Alert>
                  <li style={{ marginBottom: 16 }}>
                    Complete all required fields using black ink. This includes:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 12 }}>
                      Payer&apos;s information
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Recipient&apos;s information
                    </li>
                    <li style={{ marginBottom: 12 }}>Transaction details</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Make a copy for your records.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Mail the original form to the IRS address specified in the
                    Form 1099 General Instructions. The address varies based on
                    your state. Refer to the Where to send{' '}
                    <Link
                      href="#1099-k-mailing-address"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 1099-K - Mailing address
                    </Link>{' '}
                    section to get the address for your state.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Send a copy to the recipient by February 15th of the year
                    following the reportable transaction.
                  </li>
                </ol>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important considerations for paper filing
                  </Typography>
                  <ul style={{ marginBottom: 40 }}>
                    <li style={{ marginBottom: 16 }}>
                      Mail forms several weeks before the deadline to ensure
                      timely arrival.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Use certified mail for proof of timely filing.
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      Ensure all copies are legible.
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Alternatively, you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-k/"
                    sx={{ color: '#0202ff' }}
                    id="1099-k-mailing-address"
                  >
                    file Form 1099-K electronically
                  </Link>{' '}
                  for faster processing and delivery, reducing the risk of
                  delays or lost forms.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-K - Mailing address
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you prefer paper filing, the mailing address for Form
                  1099-K varies depending on your business location. Below is a
                  table summarizing the mailing address for Form 1099-K:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-K to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            Austin Submission Processing Center
                            <br />
                            P.O. Box 149213
                            <br />
                            Austin, TX 78714
                            <br />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            1973 North Rulon White Blvd.
                            <br />
                            Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Internal Revenue Service,
                            <br /> Austin Submission Processing Center,
                            <br /> P.O. Box 149213, <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to E-File Form 1099-K with TaxZerone?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before you start e-filing Form 1099-K, ensure you have all the
                  necessary information to facilitate a smooth and efficient
                  filing experience. Here’s what you need to gather:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Required information for filing Form 1099-K:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>Filer details</li>
                  <li style={{ marginBottom: 16 }}>Payee details</li>
                  <li style={{ marginBottom: 16 }}>
                    Indication of whether you&apos;re a payment settlement
                    entity (PSE) or electronic payment facilitator (EPF)/other
                    third-party
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Gross amount of payment card/third party network
                    transactions
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Card Not Present transactions
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Number of payment transactions
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Total reportable transactions for each month
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Federal and state income tax withheld if any
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Steps to e-file Form 1099-K using TaxZerone:
                </Typography>

                <Typography variant="body1" sx={{ mt: 1.5, mb: 2 }}>
                  <b>Step 1: Complete Form 1099-K</b>
                  <br />
                  Open Form 1099-K and fill out the necessary fields with the
                  information you have gathered.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2: Review and transmit the return</b>
                  <br />
                  Carefully review all the entered information to ensure
                  accuracy. Once verified, transmit the completed Form 1099-K to
                  the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Step 3: Provide the payee copy</b>
                  <br />
                  After successfully transmitting the form to the IRS, send a
                  copy of the completed Form 1099-K to the recipient.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of E-Filing Form 1099-K with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  <Link
                    href="/information-returns/efile-irs-form-1099-k/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    E-filing Form 1099-K
                  </Link>{' '}
                  with TaxZerone offers several advantages:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        IRS Form Validations:
                      </span>{' '}
                      TaxZerone&apos;s system checks your returns for errors or
                      missing information, ensuring accuracy before submission.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Email Recipient Copies:
                      </span>{' '}
                      Conveniently send recipient copies of Form 1099-K via
                      email, eliminating the need for manual mailing.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Secure Portal Access (ZeroneVault):
                      </span>{' '}
                      Recipients can access their return copies anytime,
                      anywhere through the secure ZeroneVault portal.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Competitive Pricing:
                      </span>{' '}
                      TaxZerone offers industry-leading pricing, with rates
                      starting as low as $0.59 per form, making it affordable
                      for businesses of all sizes.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  By using TaxZerone&apos;s e-filing solution, you can
                  streamline your Form 1099-K filing, save time, reduce errors,
                  and provide a better experience for your recipients.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 0,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Maximize the efficiency of your tax reporting by{' '}
              <Hidden smDown>
                <br />
              </Hidden>
              choosing TaxZerone for e-filing Form 1099-K.
            </Typography>
            <Box
              component="div"
              sx={{
                display: 'grid',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Box
                component="iframe"
                title="What is Form 2290: A Comprehensive Guide"
                src="https://www.youtube.com/embed/oOb58wq5x0s?rel=0"
                sx={{
                  width: { xs: '100%', sm: 500 },
                  height: { xs: '100%', sm: 281 },
                  border: '10px solid #333 !important',
                  borderRadius: 5,
                }}
                allowFullScreen={true}
              />

              <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: { xs: 3, sm: 4, md: 0 },
                    mb: { xs: 0, sm: 0, md: 10 },
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099kinstructions`}
                >
                  E-file Form 1099-K
                </Button>
              </Box>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099KInstructions;
